import { z } from 'zod';
import { AnalyticsEventVocabulary, ProviderEventEnvelopeSchema, } from './client-trial.js';
const OpaqueReference = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{2,199}$/);
const SafeLabel = z.string().trim().min(1).max(160)
    .refine((value) => !/[<>&\p{Cc}]/u.test(value), 'Text must not contain markup or control characters.')
    .refine((value) => !/\b(password|token|secret|bearer|api[ _-]?key|credential|cookie|prescription|diagnos(?:is|es|tic)?|patient|medical|health|symptom|dosage|medication|drug|allerg(?:y|ies))\b/i.test(value), 'Text is not permitted at this intake boundary.');
const StrongIdentitySchema = z.object({
    kind: z.enum(['PHONE', 'EMAIL']),
    value: z.string().trim().min(3).max(254),
}).strict();
const SessionPayloadSchema = z.object({
    sessionKey: OpaqueReference,
    anonymousIdentityKey: OpaqueReference,
    identity: StrongIdentitySchema.optional(),
}).strict();
const WebsiteActivityPayloadSchema = z.object({
    sessionKey: OpaqueReference,
    productReference: OpaqueReference.optional(),
    categoryReference: OpaqueReference.optional(),
    cartReference: OpaqueReference.optional(),
    orderReference: OpaqueReference.optional(),
    refundReference: OpaqueReference.optional(),
    searchTerm: SafeLabel.optional(),
    identity: StrongIdentitySchema.optional(),
}).strict();
const WooCommercePayloadSchema = z.object({
    customerReference: OpaqueReference.optional(),
    orderReference: OpaqueReference.optional(),
    cartReference: OpaqueReference.optional(),
    productReference: OpaqueReference.optional(),
    refundReference: OpaqueReference.optional(),
    cancellationReason: z.enum(['customer_changed_mind', 'payment_failed', 'inventory_unavailable', 'address_unserviceable']).optional(),
    identity: StrongIdentitySchema.optional(),
}).strict();
const BConnectPayloadSchema = z.object({
    sourceFilename: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._ -]{0,119}\.(?:csv|xlsx)$/i),
    contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
    sourceBytes: z.number().int().positive().max(26_214_400),
    parserProvenance: z.object({ adapter: z.literal('BCONNECT'), format: z.enum(['CSV', 'XLSX']), version: OpaqueReference }).strict(),
}).strict();
const ChannelPayloadSchema = z.object({
    conversationKey: OpaqueReference,
    displayMessage: SafeLabel,
    identity: StrongIdentitySchema,
    campaignReference: OpaqueReference.optional(),
    referralReference: OpaqueReference.optional(),
}).strict();
const websiteEvents = new Set(AnalyticsEventVocabulary);
const wooCommerceEvents = new Set([
    'customer_observed', 'product_viewed', 'add_to_cart', 'remove_from_cart', 'cart_viewed',
    'checkout_started', 'checkout_progressed', 'order_created', 'order_completed',
    'order_failed', 'order_cancelled', 'refund_created',
]);
const channelEvents = new Set(['inbound_message', 'outbound_message', 'referral_observed']);
const externalIdentifierKeys = new Set([
    'session', 'customer', 'order', 'cart', 'product', 'refund', 'conversation', 'campaign', 'referral', 'import',
]);
/**
 * CT-07 refines, but never redefines, the frozen ProviderEventEnvelope.  The
 * adapter accepts only synthetic trial events with source-specific, strictly
 * bounded payloads and opaque external references.
 */
export const SyntheticProviderEventSchema = ProviderEventEnvelopeSchema.superRefine((event, context) => {
    if (event.sourceMode !== 'SYNTHETIC_TRIAL') {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceMode'], message: 'CT-07 accepts synthetic trial events only.' });
        return;
    }
    if (Date.parse(event.receivedAt) < Date.parse(event.sourceOccurredAt)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['receivedAt'], message: 'receivedAt cannot precede sourceOccurredAt.' });
    }
    const identifiers = Object.entries(event.externalIdentifiers);
    if (identifiers.length > 10 || identifiers.some(([key, value]) => !externalIdentifierKeys.has(key) || !OpaqueReference.safeParse(value).success)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['externalIdentifiers'], message: 'External identifiers must use the approved opaque reference vocabulary.' });
    }
    let result;
    switch (event.sourceKind) {
        case 'WEBSITE':
            if (!websiteEvents.has(event.eventType)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ['eventType'], message: 'Website event type is not in the frozen vocabulary.' });
                return;
            }
            result = (event.eventType === 'session_started' ? SessionPayloadSchema : WebsiteActivityPayloadSchema).safeParse(event.payload);
            break;
        case 'WOOCOMMERCE':
            if (!wooCommerceEvents.has(event.eventType)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ['eventType'], message: 'WooCommerce event type is not approved.' });
                return;
            }
            result = WooCommercePayloadSchema.safeParse(event.payload);
            break;
        case 'BCONNECT':
            if (event.eventType !== 'catalog_import_received' || event.branchId === null) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ['eventType'], message: 'B-Connect intake requires a branch-scoped catalog_import_received event.' });
                return;
            }
            result = BConnectPayloadSchema.safeParse(event.payload);
            break;
        case 'WHATSAPP':
            if (!new Set(['inbound_message', 'outbound_message']).has(event.eventType)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ['eventType'], message: 'WhatsApp event type is not approved.' });
                return;
            }
            result = ChannelPayloadSchema.safeParse(event.payload);
            break;
        case 'INSTAGRAM':
        case 'FACEBOOK':
            if (!channelEvents.has(event.eventType)) {
                context.addIssue({ code: z.ZodIssueCode.custom, path: ['eventType'], message: 'Social channel event type is not approved.' });
                return;
            }
            result = ChannelPayloadSchema.safeParse(event.payload);
            break;
    }
    if (!result.success) {
        for (const issue of result.error.issues)
            context.addIssue({ ...issue, path: ['payload', ...issue.path] });
    }
});
export function adaptSyntheticProviderEvent(input) {
    return SyntheticProviderEventSchema.parse(input);
}
export const ProviderActivityQuerySchema = z.object({
    organizationId: z.string().uuid(),
}).strict();
export const ProviderActivityItemSchema = z.object({
    id: z.string().uuid(),
    sourceKind: z.enum(['WEBSITE', 'WOOCOMMERCE', 'BCONNECT', 'WHATSAPP', 'INSTAGRAM', 'FACEBOOK']),
    eventType: z.string().min(3).max(128),
    sourceOccurredAt: z.string(),
    receivedAt: z.string(),
    branchId: z.string().uuid().nullable(),
    domainRecordType: z.enum(['JOURNEY_SESSION', 'JOURNEY_EVENT', 'CONVERSATION_EVENT', 'CATALOG_IMPORT', 'PROVIDER_EVENT']).nullable(),
    domainRecordId: z.string().uuid().nullable(),
}).strict();
export const ProviderActivityResponseSchema = z.object({
    success: z.literal(true),
    data: z.array(ProviderActivityItemSchema).max(100),
}).strict();
export const ProviderIntakeResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({ id: z.string().uuid(), domainRecordId: z.string().uuid().nullable() }).strict(),
}).strict();
//# sourceMappingURL=integrations.js.map